import type {
  ArtifactKind,
  ChatStreamEvent,
  InterruptPrompt,
  InterruptResolveRequest,
  SkillToolKey,
} from '@innermate/shared';
import { parseSseEventBlock } from './sse-parse';

export interface SendChatMessageResult {
  messageId: string;
  /** user 메시지 INSERT 시각부터 done emit 시각까지의 wall-clock ms (서버 측정). */
  responseTimeMs: number;
}

export interface ToolCallStreamEvent {
  toolCallId: string;
  toolKey: SkillToolKey;
}

export interface ToolCallOutputEvent {
  toolCallId: string;
  status: 'ok' | 'error';
  summary: string;
  details?: unknown;
}

export interface ArtifactCreatedEvent {
  artifactId: string;
  kind: ArtifactKind;
  title: string;
  version: number;
  summary: string | null;
}

export interface ArtifactUpdatedEvent {
  artifactId: string;
  version: number;
  changeNote: string;
}

export interface AwaitUserInputEvent {
  interruptId: string;
  prompt: InterruptPrompt;
}

export interface SendChatMessageOptions {
  fileIds?: string[];
  endpointId?: string;
  bondingId?: string;
  extendedThinking?: boolean;
  /** 마지막 user 메시지 편집·재전송 시, 교체 대상 user 메시지 id (서버가 그 이후를 선삭제). */
  replaceFromMessageId?: string;
  onDelta?: (v: string) => void;
  onThinking?: (v: string) => void;
  onToolCallStart?: (ev: ToolCallStreamEvent) => void;
  onToolCallInputDelta?: (toolCallId: string, deltaJson: string) => void;
  onToolCallOutput?: (ev: ToolCallOutputEvent) => void;
  onArtifactCreated?: (ev: ArtifactCreatedEvent) => void;
  onArtifactUpdated?: (ev: ArtifactUpdatedEvent) => void;
  /**
   * 스트림이 정상 종료되지만 한도 초과·툴 연속 실패 등으로 assistant turn 이
   * 조기 중단됐음을 알리는 경고. 호출부는 에러 배너로 표시하되 대화 상태는 유지한다.
   */
  onWarning?: (message: string) => void;
  /**
   * 진행 상황 안내 (예: "엑셀 파일 처리 중..."). delta/done 이 오면 화면에서 소거해야 한다.
   */
  onStatus?: (message: string) => void;
  /**
   * 모델이 비정상 출력(무한 반복/끝없는 생성)을 내 스트림을 중단하고 다시 생성할 때.
   * 호출부는 진행 중 assistant 출력(스트림 세그먼트)을 즉시 비워야 한다 — 이어서 새 delta 가
   * 처음부터 다시 흐른다.
   */
  onReset?: () => void;
  /** Human-in-the-loop: 서버가 사용자 응답을 대기. 클라이언트는 모달로 prompt 표시. */
  onAwaitUserInput?: (ev: AwaitUserInputEvent) => void;
  /** 인터럽트가 해소됨 (resolve / skip / cancel). 모달 닫기 신호. */
  onInterruptResolved?: (interruptId: string) => void;
  /**
   * 챗 turn 시작 시 1회 — 추정 토큰 / 컨텍스트 윈도우. 헤더 chip 갱신용.
   * 80% 이상이면 amber, 95% 이상이면 빨간색.
   */
  onContextUsage?: (info: {
    estimatedTokens: number;
    contextWindow: number;
    usageRatio: number;
  }) => void;
  /** 스트림 정상 종료 시 서버가 측정한 응답 시간(ms) 을 전달. 실시간 타이머 정지에 사용. */
  onDone?: (responseTimeMs: number) => void;
  signal?: AbortSignal;
}

/**
 * SSE body 를 토큰 단위로 파싱해 콜백을 호출. POST 신규 메시지와 GET follow 둘 다 사용.
 */
async function consumeStream(
  res: Response,
  opts: SendChatMessageOptions,
): Promise<SendChatMessageResult> {
  if (!res.body) throw new Error('응답 body 가 비어 있습니다.');
  const reader = res.body.pipeThrough(new TextDecoderStream()).getReader();
  let buf = '';
  let done: SendChatMessageResult | null = null;
  // mid-stream 에서 fetch reader 가 깨지면 (HTTP/2 reset, idle timeout 등) reader.read() 가
  // TypeError / NetworkError 를 throw 한다. 그대로 두면 mutation onError 가 받는 객체가 항상
  // Error 인스턴스라는 보장을 잃어 라이브러리 내부 후처리 (`.payload` 접근 등) 가 깨질 수
  // 있다. AbortError 만 그대로 통과시키고 나머지는 깔끔한 Error 로 정규화.
  try {
    while (true) {
      const { value, done: finished } = await reader.read();
      if (finished) break;
      if (value) buf += value;
      const events = buf.split('\n\n');
      buf = events.pop() ?? '';
      // follow replay 로 await_user_input → interrupt_resolved 가 같은 청크에 같이 오면
      // 모달이 한 프레임 떴다가 닫혀 깜빡인다. 같은 청크 안에서 이미 resolved 된 interruptId
      // 의 await_user_input 은 디스패치를 건너뛴다.
      const resolvedInChunk = new Set<string>();
      const parsedPayloads: ChatStreamEvent[] = [];
      for (const ev of events) {
        const json = parseSseEventBlock(ev);
        if (!json) continue;
        let payload: ChatStreamEvent;
        try {
          payload = JSON.parse(json) as ChatStreamEvent;
        } catch {
          continue;
        }
        if (payload.t === 'interrupt_resolved') resolvedInChunk.add(payload.interruptId);
        parsedPayloads.push(payload);
      }
      for (const payload of parsedPayloads) {
        if (payload.t === 'delta') {
          opts.onDelta?.(payload.v);
        } else if (payload.t === 'thinking') {
          opts.onThinking?.(payload.v);
        } else if (payload.t === 'tool_call_start') {
          opts.onToolCallStart?.({ toolCallId: payload.toolCallId, toolKey: payload.toolKey });
        } else if (payload.t === 'tool_call_input_delta') {
          opts.onToolCallInputDelta?.(payload.toolCallId, payload.deltaJson);
        } else if (payload.t === 'tool_call_output') {
          opts.onToolCallOutput?.({
            toolCallId: payload.toolCallId,
            status: payload.status,
            summary: payload.summary,
            details: payload.details,
          });
        } else if (payload.t === 'artifact_created') {
          opts.onArtifactCreated?.({
            artifactId: payload.artifactId,
            kind: payload.kind,
            title: payload.title,
            version: payload.version,
            summary: payload.summary,
          });
        } else if (payload.t === 'artifact_updated') {
          opts.onArtifactUpdated?.({
            artifactId: payload.artifactId,
            version: payload.version,
            changeNote: payload.changeNote,
          });
        } else if (payload.t === 'done') {
          done = { messageId: payload.messageId, responseTimeMs: payload.responseTimeMs };
          // ackOnly=true 는 /resolve Fast-path 의 짧은 ack — 실제 LLM iteration 은 원래
          // /messages SSE 에서 계속 흐름. onDone (UI 리셋) 호출하면 streamSegments / isStreaming
          // 이 비워져 이후 라이브 이벤트가 렌더 안 되고 끝나서 한 번에 표시되는 회귀를 일으킴.
          if (!payload.ackOnly) {
            opts.onDone?.(payload.responseTimeMs);
          }
        } else if (payload.t === 'warning') {
          opts.onWarning?.(payload.message);
        } else if (payload.t === 'reset') {
          opts.onReset?.();
        } else if (payload.t === 'status') {
          opts.onStatus?.(payload.message);
        } else if (payload.t === 'await_user_input') {
          const { t: _t, interruptId, ...prompt } = payload;
          // 같은 청크에 interrupt_resolved 가 같이 있으면 디스패치 skip — replay 깜빡임 방지.
          if (resolvedInChunk.has(interruptId)) continue;
          opts.onAwaitUserInput?.({ interruptId, prompt });
        } else if (payload.t === 'interrupt_resolved') {
          opts.onInterruptResolved?.(payload.interruptId);
        } else if (payload.t === 'context_usage') {
          opts.onContextUsage?.({
            estimatedTokens: payload.estimatedTokens,
            contextWindow: payload.contextWindow,
            usageRatio: payload.usageRatio,
          });
        } else if (payload.t === 'error') {
          throw new Error(payload.message);
        }
      }
    }
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    if (err instanceof Error) throw err;
    throw new Error('스트림 수신 실패');
  }
  if (!done) throw new Error('스트림이 정상 종료되지 않았습니다.');
  return done;
}

/**
 * SSE 스트림으로 사용자 메시지를 보내고 어시스턴트 응답을 토큰 단위로 수신.
 */
export async function sendChatMessage(
  sessionId: string,
  content: string,
  opts: SendChatMessageOptions = {},
): Promise<SendChatMessageResult> {
  const body: {
    content: string;
    fileIds?: string[];
    endpointId?: string;
    bondingId?: string;
    extendedThinking?: boolean;
    replaceFromMessageId?: string;
  } = { content };
  if (opts.fileIds && opts.fileIds.length > 0) body.fileIds = opts.fileIds;
  if (opts.endpointId) body.endpointId = opts.endpointId;
  if (opts.bondingId) body.bondingId = opts.bondingId;
  if (opts.extendedThinking !== undefined) body.extendedThinking = opts.extendedThinking;
  if (opts.replaceFromMessageId) body.replaceFromMessageId = opts.replaceFromMessageId;

  const res = await fetch(`/api/chat/sessions/${encodeURIComponent(sessionId)}/messages`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
    signal: opts.signal,
  });
  if (!res.ok || !res.body) {
    let message = `요청 실패 (${res.status})`;
    try {
      const text = await res.text();
      const json = text ? JSON.parse(text) : null;
      if (json?.error?.message) message = json.error.message;
    } catch {
      // ignore
    }
    throw new Error(message);
  }
  return consumeStream(res, opts);
}

/**
 * 새로고침/창닫기 후 재진입 시 진행 중 응답에 합류. 진행 중 stream 이 없으면 null 반환.
 * 콜백 시그니처는 sendChatMessage 와 동일 — 호출부가 같은 핸들러 묶음을 재사용할 수 있다.
 */
export async function followChatStream(
  sessionId: string,
  opts: SendChatMessageOptions = {},
): Promise<SendChatMessageResult | null> {
  const res = await fetch(`/api/chat/sessions/${encodeURIComponent(sessionId)}/stream`, {
    method: 'GET',
    credentials: 'include',
    signal: opts.signal,
  });
  if (res.status === 204) return null;
  if (!res.ok || !res.body) {
    let message = `요청 실패 (${res.status})`;
    try {
      const text = await res.text();
      const json = text ? JSON.parse(text) : null;
      if (json?.error?.message) message = json.error.message;
    } catch {
      // ignore
    }
    throw new Error(message);
  }
  return consumeStream(res, opts);
}

/**
 * 진행 중 응답 명시적 취소 (정지 버튼 등). active run 이 없으면 서버가 canceled=false 응답.
 */
export async function cancelChatRun(sessionId: string): Promise<void> {
  await fetch(`/api/chat/sessions/${encodeURIComponent(sessionId)}/cancel`, {
    method: 'POST',
    credentials: 'include',
  });
}

/**
 * Human-in-the-loop 인터럽트 응답 + LLM 실행을 한 SSE 요청에 묶음. 사용자가 모달에서 선택/직접입력/skip
 * 한 결과를 서버로 보내고, 서버는 같은 응답으로 done (skip) 또는 LLM stream 을 흘려 보낸다.
 */
export async function resolveInterruptStream(
  sessionId: string,
  interruptId: string,
  body: InterruptResolveRequest,
  opts: SendChatMessageOptions = {},
): Promise<SendChatMessageResult> {
  const res = await fetch(
    `/api/chat/sessions/${encodeURIComponent(sessionId)}/interrupts/${encodeURIComponent(
      interruptId,
    )}/resolve`,
    {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
      signal: opts.signal,
    },
  );
  if (!res.ok || !res.body) {
    let message = `요청 실패 (${res.status})`;
    try {
      const text = await res.text();
      const json = text ? JSON.parse(text) : null;
      if (json?.error?.message) message = json.error.message;
    } catch {
      // ignore
    }
    throw new Error(message);
  }
  return consumeStream(res, opts);
}
